import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronDown, 
  ChevronRight, 
  Eye, 
  Edit, 
  Trash2, 
  Download,
  Mail,
  CheckCircle,
  Clock,
  AlertCircle,
  MoreVertical,
  Receipt,
  FileText,
  RotateCcw,
  CreditCard,
  DollarSign
} from 'lucide-react';
import { InvoiceDto, InvoiceStatus, WorkOrderDto, InvoicePaymentSummary } from '../../types';
import ClientDisplay from '../common/ClientDisplay';
import PaymentProgressIndicator from '../invoice/PaymentProgressIndicator';
import { formatTimestamp } from '../../utils/formatters';
import { formatCurrency } from '../../utils/currency';
import { useCompanyTimeZone } from '../../hooks/useCompanyTimeZone';

interface GroupedInvoiceGridProps {
  invoices: InvoiceDto[];
  isLoading?: boolean;
  paymentSummaries?: Map<number, InvoicePaymentSummary>; // Map of workOrderId to payment summary
  onViewDetails: (invoice: InvoiceDto) => void;
  onEditInvoice: (invoice: InvoiceDto) => void;
  onDeleteInvoice?: (invoice: InvoiceDto) => void;
  onMarkAsSent?: (invoice: InvoiceDto) => void;
  onMarkAsPaid?: (invoice: InvoiceDto) => void;
  onCreateCreditMemo?: (invoice: InvoiceDto) => void;
  onManagePayments?: (invoice: InvoiceDto) => void;
  onDownloadPDF?: (invoice: InvoiceDto) => void;
  onViewWorkOrder?: (workOrder: WorkOrderDto) => void;
  onRestoreInvoice?: (invoice: InvoiceDto) => void;
  showDeletedInfo?: boolean;
  onArchiveInvoice?: (invoice: InvoiceDto) => void;
  onUnarchiveInvoice?: (invoice: InvoiceDto) => void;
  // Bulk operations
  onBulkMarkAsSent?: (invoiceIds: number[], sendEmail: boolean) => void;
  onBulkArchive?: (invoiceIds: number[]) => void;
  onBulkExport?: (invoiceIds: number[]) => void;
}

interface WorkOrderGroup {
  workOrder: WorkOrderDto;
  invoices: InvoiceDto[];
  totalAmount: number;
  isExpanded: boolean;
}

const GroupedInvoiceGrid: React.FC<GroupedInvoiceGridProps> = ({
  invoices,
  isLoading,
  paymentSummaries,
  onViewDetails,
  onEditInvoice,
  onDeleteInvoice,
  onMarkAsSent,
  onMarkAsPaid,
  onCreateCreditMemo,
  onManagePayments,
  onDownloadPDF,
  onViewWorkOrder,
  onRestoreInvoice,
  showDeletedInfo = false,
  onArchiveInvoice,
  onUnarchiveInvoice,
  onBulkMarkAsSent,
  onBulkArchive,
  onBulkExport
}) => {
  const navigate = useNavigate();
  const { companyTimeZone } = useCompanyTimeZone();
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdownId) {
        const target = event.target as Element;
        // Check if click is on the button or inside the dropdown menu
        const isButtonClick = target.closest('.dropdown-container');
        const isDropdownClick = target.closest('[data-dropdown-menu]');
        
        if (!isButtonClick && !isDropdownClick) {
          setActiveDropdownId(null);
        }
      }
    };

    if (activeDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdownId]);

  // Group invoices by work order
  const groupedData = useMemo(() => {
    const groups = new Map<number, WorkOrderGroup>();
    
    invoices.forEach(invoice => {
      if (invoice.workOrderId && invoice.workOrder) {
        const workOrderId = invoice.workOrderId;
        
        if (!groups.has(workOrderId)) {
          groups.set(workOrderId, {
            workOrder: invoice.workOrder,
            invoices: [],
            totalAmount: 0,
            isExpanded: expandedGroupId === workOrderId
          });
        }
        
        const group = groups.get(workOrderId)!;
        group.invoices.push(invoice);
        group.totalAmount += invoice.total;
      }
    });
    
    return Array.from(groups.values()).sort((a, b) => 
      new Date(b.workOrder.createdDate).getTime() - new Date(a.workOrder.createdDate).getTime()
    );
  }, [invoices, expandedGroupId]);

  const toggleGroup = (workOrderId: number) => {
    if (expandedGroupId === workOrderId) {
      setExpandedGroupId(null); // Close if already open
    } else {
      setExpandedGroupId(workOrderId); // Open this one and close others
    }
  };

  // Bulk selection handlers
  const toggleInvoiceSelection = (invoiceId: number) => {
    const newSelection = new Set(selectedInvoices);
    if (newSelection.has(invoiceId)) {
      newSelection.delete(invoiceId);
    } else {
      newSelection.add(invoiceId);
    }
    setSelectedInvoices(newSelection);
    setShowBulkActions(newSelection.size > 0);
  };

  const selectAllInvoices = () => {
    const allInvoiceIds = new Set(invoices.map(invoice => invoice.id));
    setSelectedInvoices(allInvoiceIds);
    setShowBulkActions(true);
  };

  const clearSelection = () => {
    setSelectedInvoices(new Set());
    setShowBulkActions(false);
  };

  const handleBulkMarkAsSent = () => {
    if (onBulkMarkAsSent && selectedInvoices.size > 0) {
      onBulkMarkAsSent(Array.from(selectedInvoices), false);
      clearSelection();
    }
  };

  const handleBulkArchive = () => {
    if (onBulkArchive && selectedInvoices.size > 0) {
      onBulkArchive(Array.from(selectedInvoices));
      clearSelection();
    }
  };

  const handleBulkExport = () => {
    if (onBulkExport && selectedInvoices.size > 0) {
      onBulkExport(Array.from(selectedInvoices));
      clearSelection();
    }
  };

  const getStatusBadge = (status: InvoiceStatus | string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200';
      case 'archived':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: InvoiceStatus | string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return <Clock className="h-3 w-3" />;
      case 'sent':
        return <Mail className="h-3 w-3" />;
      case 'paid':
        return <CheckCircle className="h-3 w-3" />;
      case 'partial':
        return <DollarSign className="h-3 w-3" />;
      case 'overdue':
        return <AlertCircle className="h-3 w-3" />;
      case 'cancelled':
        return <AlertCircle className="h-3 w-3" />;
      case 'archived':
        return <RotateCcw className="h-3 w-3 rotate-180" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const getConsolidatedStatus = (invoices: InvoiceDto[]): string => {
    if (invoices.length === 0) return 'No Invoices';
    
    const statuses = invoices.map(inv => inv.status.toLowerCase());
    
    // Priority order: Paid > Partial > Sent > Overdue > Cancelled > Draft
    if (statuses.includes('paid')) return 'Paid';
    if (statuses.includes('partial')) return 'Partial';
    if (statuses.includes('sent')) return 'Sent';
    if (statuses.includes('overdue')) return 'Overdue';
    if (statuses.includes('cancelled')) return 'Cancelled';
    if (statuses.includes('draft')) return 'Draft';
    
    return 'Unknown';
  };

  const getConsolidatedStatusBadge = (invoices: InvoiceDto[]): string => {
    const status = getConsolidatedStatus(invoices);
    return getStatusBadge(status as InvoiceStatus);
  };

  const getConsolidatedStatusIcon = (invoices: InvoiceDto[]): React.ReactNode => {
    const status = getConsolidatedStatus(invoices);
    return getStatusIcon(status as InvoiceStatus);
  };

  const handleDropdownAction = (action: string, invoice: InvoiceDto) => {
    setActiveDropdownId(null);
    
    switch (action) {
      case 'view':
        onViewDetails(invoice);
        break;
      case 'edit':
        onEditInvoice(invoice);
        break;
      case 'delete':
        onDeleteInvoice(invoice);
        break;
      case 'markSent':
        onMarkAsSent(invoice);
        break;
      case 'markPaid':
        onMarkAsPaid(invoice);
        break;
      case 'createCreditMemo':
        onCreateCreditMemo(invoice);
        break;
      case 'managePayments':
        onManagePayments(invoice);
        break;
      case 'download':
        onDownloadPDF?.(invoice);
        break;
      case 'restore':
        onRestoreInvoice?.(invoice);
        break;
      case 'archive':
        onArchiveInvoice?.(invoice);
        break;
      case 'unarchive':
        onUnarchiveInvoice?.(invoice);
        break;
    }
  };

  const renderDropdown = (invoice: InvoiceDto) => {
    const isActive = activeDropdownId === `invoice-${invoice.id}`;
    
    const calculatePosition = (buttonElement: HTMLElement) => {
      const rect = buttonElement.getBoundingClientRect();
      const dropdownHeight = 250;
      const dropdownWidth = 224;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top = rect.bottom + window.scrollY + 4;
      let left = rect.left + window.scrollX;
      
      // Position above if not enough space below
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4;
      }
      
      // Adjust horizontal position to keep within viewport
      if (left + dropdownWidth > viewportWidth) {
        left = viewportWidth - dropdownWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }
      
      return { top, left };
    };
    
    const handleButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (activeDropdownId === `invoice-${invoice.id}`) {
        setActiveDropdownId(null);
      } else {
        const position = calculatePosition(e.currentTarget as HTMLElement);
        setDropdownPosition(position);
        setActiveDropdownId(`invoice-${invoice.id}`);
      }
    };
    
    return (
      <div className="relative dropdown-container">
        <button 
          className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color-500 dark:focus:ring-offset-gray-900" 
          onClick={handleButtonClick}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      
        {isActive && createPortal(
          <div 
            data-dropdown-menu
            className="fixed w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[9999]"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999
            }}
          >
            <div className="py-1">
              <button
                onClick={() => handleDropdownAction('view', invoice)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Eye className="h-4 w-4 mr-3" />
                View Details
              </button>
              {!['Paid', 'Cancelled'].includes(invoice.status) && (
                <button
                  onClick={() => handleDropdownAction('edit', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Edit className="h-4 w-4 mr-3" />
                  Edit Invoice
                </button>
              )}
              {onDownloadPDF && (
                <button
                  onClick={() => handleDropdownAction('download', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Download className="h-4 w-4 mr-3" />
                  Download PDF
                </button>
              )}
              {invoice.status === InvoiceStatus.Draft && (
                <button
                  onClick={() => handleDropdownAction('markSent', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Mail className="h-4 w-4 mr-3" />
                  Mark as Sent
                </button>
              )}
              {invoice.status === InvoiceStatus.Sent && (
                <button
                  onClick={() => handleDropdownAction('markPaid', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <CheckCircle className="h-4 w-4 mr-3" />
                  Mark as Paid
                </button>
              )}
              {onManagePayments && (invoice.status === 'Sent' || invoice.status === 'Paid' || invoice.status === 'Partial' || invoice.status === 'Overdue') && (
                <button
                  onClick={() => handleDropdownAction('managePayments', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <DollarSign className="h-4 w-4 mr-3" />
                  Manage Payments
                </button>
              )}
              {/* Archive/Unarchive */}
              {onArchiveInvoice && invoice.status === InvoiceStatus.Paid && (
                <button
                  onClick={() => handleDropdownAction('archive', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <RotateCcw className="h-4 w-4 mr-3 rotate-180" />
                  Archive Invoice
                </button>
              )}
              {onUnarchiveInvoice && (invoice.status as any) === 'Archived' && (
                <button
                  onClick={() => handleDropdownAction('unarchive', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <RotateCcw className="h-4 w-4 mr-3" />
                  Unarchive Invoice
                </button>
              )}
              {onCreateCreditMemo && (
                <button
                  onClick={() => handleDropdownAction('createCreditMemo', invoice)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <CreditCard className="h-4 w-4 mr-3" />
                  Create Credit Memo
                </button>
              )}
              {showDeletedInfo ? (
                onRestoreInvoice && (
                  <button
                    onClick={() => handleDropdownAction('restore', invoice)}
                    className="flex items-center w-full px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    <RotateCcw className="h-4 w-4 mr-3" />
                    Restore Invoice
                  </button>
                )
              ) : (
                onDeleteInvoice && (
                  <button
                    onClick={() => handleDropdownAction('delete', invoice)}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4 mr-3" />
                    Delete Invoice
                  </button>
                )
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-color-600"></div>
      </div>
    );
  }

  if (groupedData.length === 0) {
    return (
      <div className="text-center py-12">
        <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No invoices found
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Create your first invoice to get started
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="bg-accent-color-50 dark:bg-accent-color-900/20 border-b border-accent-color-200 dark:border-accent-color-800 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-accent-color-900 dark:text-accent-color-100">
                {selectedInvoices.size} invoice{selectedInvoices.size === 1 ? '' : 's'} selected
              </span>
              <div className="flex items-center space-x-2">
                {onBulkMarkAsSent && (
                  <button
                    onClick={handleBulkMarkAsSent}
                    className="px-3 py-1 text-xs font-medium text-accent-color-700 bg-accent-color-100 hover:bg-accent-color-200 dark:text-accent-color-300 dark:bg-accent-color-800 dark:hover:bg-accent-color-700 rounded-md transition-colors"
                  >
                    Mark as Sent
                  </button>
                )}
                {onBulkArchive && (
                  <button
                    onClick={handleBulkArchive}
                    className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 dark:text-purple-300 dark:bg-purple-800 dark:hover:bg-purple-700 rounded-md transition-colors"
                  >
                    Archive Selected
                  </button>
                )}
                {onBulkExport && (
                  <button
                    onClick={handleBulkExport}
                    className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-800 dark:hover:bg-green-700 rounded-md transition-colors"
                  >
                    Export to CSV
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={selectAllInvoices}
                className="text-xs text-accent-color-600 hover:text-accent-color-800 dark:text-accent-color-400 dark:hover:text-accent-color-300"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table
          className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed"
          style={{
            // Shared column widths used by header and invoice rows
            // Keep sums ~100% to avoid horizontal scroll
            // @ts-ignore - CSS custom properties
            ['--col1' as any]: '24%',
            ['--col2' as any]: '18%',
            ['--col3' as any]: '10%',
            ['--col4' as any]: '10%',
            ['--col5' as any]: '12%',
            ['--col6' as any]: '14%',
            ['--col7' as any]: '12%'
          }}
        >
          <colgroup>
            <col style={{ width: 'var(--col1)' }} />
            <col style={{ width: 'var(--col2)' }} />
            <col style={{ width: 'var(--col3)' }} />
            <col style={{ width: 'var(--col4)' }} />
            <col style={{ width: 'var(--col5)' }} />
            <col style={{ width: 'var(--col6)' }} />
            <col style={{ width: 'var(--col7)' }} />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th style={{width: 'var(--col1)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Work Order / Invoice
              </th>
                  <th style={{width: 'var(--col2)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Client
                  </th>
              <th style={{width: 'var(--col3)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Date
              </th>
              <th style={{width: 'var(--col4)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Due Date
              </th>
              <th style={{width: 'var(--col5)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Total
              </th>
              <th style={{width: 'var(--col6)'}} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th style={{width: 'var(--col7)'}} className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {groupedData.map((group) => (
              <React.Fragment key={group.workOrder.id}>
                {/* Work Order Header Row */}
                <tr
                  className="bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-all duration-200 ease-in-out hover:shadow-sm"
                  onClick={() => toggleGroup(group.workOrder.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleGroup(group.workOrder.id)}
                        className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 ease-in-out hover:scale-110"
                      >
                        <ChevronRight className={`h-4 w-4 text-gray-500 transition-all duration-300 ease-in-out ${group.isExpanded ? 'rotate-90' : 'rotate-0'}`} />
                      </button>
                      <div className="flex items-center space-x-2">
                        <FileText className="h-5 w-5 text-accent-color-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            WO #{group.workOrder.workOrderNumber}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ClientDisplay
                      clientName={group.workOrder.clientName || group.invoices[0]?.clientName || 'N/A'}
                      locationName={group.workOrder.clientLocationName || group.invoices[0]?.clientLocationName}
                      streetAddress={group.workOrder.clientAddress || group.invoices[0]?.clientAddress}
                      city={group.workOrder.clientCity || group.invoices[0]?.clientCity}
                      state={group.workOrder.clientState || group.invoices[0]?.clientState}
                      zipCode={group.workOrder.clientZipCode || group.invoices[0]?.clientZipCode}
                      contactPhone={group.workOrder.clientLocationContactPhone || group.workOrder.clientPhone || group.invoices[0]?.clientPhone}
                      contactEmail={group.workOrder.clientLocationContactEmail || group.workOrder.clientEmail || group.invoices[0]?.clientEmail}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {new Date(group.workOrder.createdDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {group.workOrder.dueDate ? new Date(group.workOrder.dueDate).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {group.invoices.length > 0 ? formatCurrency(group.totalAmount, group.invoices[0].currencyCode) : '$0.00'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {paymentSummaries?.get(group.workOrder.id) ? (
                        <>
                          {console.log('Rendering payment progress for work order', group.workOrder.id, paymentSummaries.get(group.workOrder.id))}
                          <PaymentProgressIndicator
                            totalInvoiced={paymentSummaries.get(group.workOrder.id)!.totalInvoiced}
                            totalPaid={paymentSummaries.get(group.workOrder.id)!.totalPaid}
                            paymentPercentage={paymentSummaries.get(group.workOrder.id)!.paymentPercentage}
                            compact={true}
                          />
                        </>
                      ) : (
                        <>
                          {console.log('No payment summary found for work order', group.workOrder.id, 'paymentSummaries:', paymentSummaries)}
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getConsolidatedStatusBadge(group.invoices)}`}
                          >
                            {getConsolidatedStatusIcon(group.invoices)}
                            <span className="ml-1">{getConsolidatedStatus(group.invoices)}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {onViewWorkOrder && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onViewWorkOrder(group.workOrder); }}
                        className="text-accent-color-600 hover:text-accent-color-900 dark:text-accent-color-400 dark:hover:text-accent-color-300"
                      >
                        View WO
                      </button>
                    )}
                  </td>
                </tr>

                {/* Invoice Rows Container */}
                <tr>
                  <td colSpan={7} className="p-0">
                    <div 
                      className={`overflow-hidden transition-[max-height] duration-150 ease-out ${
                        group.isExpanded 
                          ? 'max-h-[2000px]' 
                          : 'max-h-0'
                      }`}
                    >
                      <div className="bg-gray-50 dark:bg-gray-700/30">
                        {group.invoices.map((invoice, index) => (
                          <div
                            key={invoice.id}
                            className="bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600/50 cursor-pointer hover:shadow-sm"
                            onClick={(e) => {
                              // Don't trigger if clicking on the actions dropdown or dropdown menu
                              const target = e.target as HTMLElement;
                              if (!target.closest('.dropdown-container') && !target.closest('[data-dropdown-menu]')) {
                                onViewDetails(invoice);
                              }
                            }}
                          >
                            <div
                              className="grid gap-0 px-0 py-4 items-stretch"
                              style={{
                                gridTemplateColumns: 'var(--col1) var(--col2) var(--col3) var(--col4) var(--col5) var(--col6) var(--col7)'
                              }}
                            >
                              {/* Invoice Number Column */}
                              <div className="flex items-center space-x-2 h-full px-6">
                                <input
                                  type="checkbox"
                                  checked={selectedInvoices.has(invoice.id)}
                                  onChange={() => toggleInvoiceSelection(invoice.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <Receipt className="h-4 w-4 text-gray-400" />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {invoice.invoiceNumber}
                                    </div>
                                    {invoice.invoiceTypeName && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                                        {invoice.invoiceTypeName}
                                      </span>
                                    )}
                                    {typeof invoice.sequenceNumber === 'number' && (
                                      <span className="text-[10px] text-gray-500 dark:text-gray-400">#{invoice.sequenceNumber}</span>
                                    )}
                                    {showDeletedInfo && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Deleted
                                      </span>
                                    )}
                                  </div>
                                  {invoice.depositPercentage != null && invoice.invoiceTypeName === 'Deposit' && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{invoice.depositPercentage}% Deposit</div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Client Name Column */}
                              <div className="flex items-center h-full pr-6">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {invoice.clientName || 'N/A'}
                                </div>
                              </div>
                              
                              {/* Date Column */}
                              <div className="flex items-center h-full pr-6">
                                {(() => {
                                  const primary = formatTimestamp(invoice.invoiceDate, companyTimeZone || undefined);
                                  const fallback = formatTimestamp((invoice as any).createdAt as any, companyTimeZone || undefined);
                                  const show = primary.time === '12:00 AM' && (invoice as any).createdAt ? fallback : primary;
                                  return (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      <div>{show.date}</div>
                                      <div className="text-xs">{show.time}</div>
                                    </div>
                                  );
                                })()}
                              </div>
                              
                              {/* Due Date Column */}
                              <div className="flex items-center h-full pr-6">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  <div>{formatTimestamp(invoice.dueDate, companyTimeZone || undefined).date}</div>
                                  <div className="text-xs">{formatTimestamp(invoice.dueDate, companyTimeZone || undefined).time}</div>
                                </div>
                              </div>
                              
                              {/* Amount Column */}
                              <div className="flex items-center h-full pr-6">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {formatCurrency(invoice.total, invoice.currencyCode)}
                                </div>
                              </div>
                              
                              {/* Status Column */}
                              <div className="flex items-center h-full pr-6">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                                  {getStatusIcon(invoice.status)}
                                  <span className="ml-1">{invoice.status}</span>
                                </span>
                              </div>
                              
                              {/* Actions Column */}
                              <div className="flex items-center justify-end h-full px-6">
                                {renderDropdown(invoice)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupedInvoiceGrid;
